import { Injectable, OnDestroy } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable, Subscription, firstValueFrom, of, throwError } from 'rxjs';
import { jwtDecode } from "jwt-decode";
import { Router } from '@angular/router';

import { environment } from 'src/environments/environment';
import { DatosCompartidosService } from 'src/app/features/user-sing-in/services/datos-compartidos.service';
import { UserUtilsService } from 'src/app/features/user-sing-in/services/user-utils.service';
import { Attribute } from '@angular/compiler';

@Injectable({
  providedIn: 'root'
})
export class UsuarioService implements OnDestroy {
  private intervalId: any;

  keycloak_URL_autorize = environment.URL_KEYCLOAK + 'admin/realms/' + environment.REALM_KEYCLOAK + '/users';
  keyCloakToken: string | null = null;

  gestionSSOAPIToken: string | null = null;

  constructor(private http: HttpClient,
    private router: Router,
    private datosCompartidos: DatosCompartidosService,
    private usuarioUtils: UserUtilsService) { }

  async procesarToken(tokenResolved: string) {
    const decodedToken = jwtDecode(tokenResolved);
    let idUsuarioKC = decodedToken["sub"];

    // actualizar el token despues que se autoriza el usuario
    if (this.datosCompartidos.getTokenNumber() == 0) { // el token solo se actualiza la primera vez
      // Paso 1: autorizar al Usuario asiganadole el rol de consulta
      let autorizacionExitosa = await this.autorizarUsuario(idUsuarioKC);
      if (!autorizacionExitosa) {
        console.error('No se pudo autorizar al usuario.');
        throw ('No se pudo autorizar al usuario.');
      }
      this.datosCompartidos.increaseTokenNumber();
    }
    // Paso 2: Consulta de usuarios en keycloak desde vital publico
    let usuarioKeyCloak = await this.consultaUsuarioKeycloak(idUsuarioKC);
    let registradoVital = usuarioKeyCloak.attributes["registro_vital"];
    this.datosCompartidos.setRegistradoEnVital(registradoVital);
    // si el usuario no esta registrado en vital se inicia el proceso de registro
    if (!registradoVital || registradoVital.length === 0 || registradoVital[0] == "false") {
      let usuarioVital = null;
      // Paso 3: determinar el nivel de confianza para saber si dar o no accesso al usuario
      let LOAValido = usuarioKeyCloak.attributes["LOA"] == "loa:2" || usuarioKeyCloak.attributes["LOA"] == "loa:3";
      let usuarioInput;
      if (LOAValido) {
        // Paso4: consultar usuario en Gestion-SSO-API
        const { tipoIdentificacion, numeroIdentificacion } = this.setIdentificacion(usuarioKeyCloak);
        usuarioVital = await this.consultarUsuarioVital(tipoIdentificacion, numeroIdentificacion);
        // Paso 4: Trasformar las respuestas de las peticiones del punto 3 al formato estandarizado del componente web
        await this.usuarioUtils.transformarUsuario(usuarioKeyCloak, usuarioVital).then(
          usuarioTransformado => {
            usuarioTransformado.usuarioKeycloack = usuarioKeyCloak;
            usuarioInput = usuarioTransformado;
            console.log("Usuario keycloack:", usuarioKeyCloak);
            console.log("Usuario Transformado:", usuarioTransformado);
          }
        );
      } else {
        usuarioInput = { attributes: { LOA: "loa:1" } };
      }
      // Paso 5: compartir la informacion necesaria para cargar el formulario de registro
      this.datosCompartidos.setIDUsrKeycloak(idUsuarioKC);
      this.datosCompartidos.setToken(tokenResolved);
      // Paso 5: invocar componente Web
      this.router.navigate(['/registro-usuario']);
      this.datosCompartidos.setUser(usuarioInput);
    }
  }

  async autorizarUsuario(idUsuarioKC: string) {
    if (!this.keyCloakToken) {
      try {
        const token = await this.obtenerTokenDeSession().toPromise();
        if (token) {
          let response = await this.invocarAutorizacionUsuario(idUsuarioKC).toPromise();
          return response.result;
        } else {
          console.error('No se pudo obtener el token.');
          throw new Error('No se pudo obtener el token.');
        }
      } catch (error) {
        console.error('Error al obtener el token:', error);
        throw error;
      }
    }
    return false;
  }


  invocarAutorizacionUsuario(idUsuarioKC: string): Observable<any> {
    const url = `${environment.URL_GESTION_SSO_API}/${environment.AUTORIZAR_USUARIO_ENDPOINT}?sub_usuario=${idUsuarioKC}`;
    const headers = new HttpHeaders({
      'Authorization': `Bearer ${this.keyCloakToken}`,
      'content-type': 'application/json'
    });
    return this.http.post(url, {}, { headers });
  }

  async consultarUsuarioVital(tipoIdentificacion: string, numeroIdentificacion: string): Promise<any> {
    if (!this.keyCloakToken) {
      try {
        const token = await this.obtenerTokenDeSession().toPromise();
        if (token) {
          this.keyCloakToken = token["access_token"];
        } else {
          console.error('No se pudo obtener el token.');
          throw new Error('No se pudo obtener el token.');
        }
      } catch (error) {
        console.error('Error al obtener el token:', error);
        throw error;
      }
    }

    return firstValueFrom(this.invocarServicioGSSOAPI(tipoIdentificacion, numeroIdentificacion));
  }

  async consultaUsuarioKeycloak(subUsuario: string): Promise<any> {
    if (!this.keyCloakToken) {
      try {
        const token = await this.obtenerTokenDeSession().toPromise();
        if (!token) {
          console.error('No se pudo obtener el token.');
          throw new Error('No se pudo obtener el token.');
        }
      } catch (error) {
        console.error('Error al obtener el token:', error);
        throw error;
      }
    }

    return this.usuarioUtils.invocarServicioKeycloak(subUsuario, this.keyCloakToken).toPromise();
  }



  invocarServicioGSSOAPI(tipoId: string, numreroID: string): Observable<any> {
    let endpoint;
    let params = new HttpParams();
    if (tipoId === "Nit") {
      endpoint = environment.PERSONA_JURIDICA_ENDPOINT;
      params = params.set('identificacion', numreroID);
    } else {
      endpoint = environment.PERSONA_NATURAL_ENDPOINT;
      params = params.set('tipo_identificacion', tipoId).set('num_identificacion', numreroID);
    }
    const url = `${environment.URL_GESTION_SSO_API}/${environment.PERSONA_NATURAL_ENDPOINT}`;
    const headers = new HttpHeaders({
      'Authorization': `Bearer ${this.keyCloakToken}`
    });

    return this.http.get(url, { headers, params });
  }


  obtenerTokenDeSession(): Observable<string> {
    return new Observable<string>((observer) => {
      this.intervalId = setInterval(() => {
        this.keyCloakToken = localStorage.getItem('keycloak-token');

        if (this.keyCloakToken) {
          clearInterval(this.intervalId);
          observer.next(this.keyCloakToken);
          observer.complete();
        }
      }, 2000);

      // Limpiar el intervalo si se cancela la suscripción
      return () => clearInterval(this.intervalId);
    });
  }

  ngOnDestroy(): void {
    // Limpia el intervalo si el servicio se destruye
    if (this.intervalId) {
      clearInterval(this.intervalId);
    }
  }

  setIdentificacion(usuarioKeyCloak: any): { tipoIdentificacion: string, numeroIdentificacion: string } {
    let tipoIdentificacion: string;
    let numeroIdentificacion: string;

    if (usuarioKeyCloak.attributes["tipoIdentificacion"][0] && usuarioKeyCloak.attributes["numeroIdentificacion"][0]) {
      tipoIdentificacion = usuarioKeyCloak.attributes["tipoIdentificacion"][0];
      numeroIdentificacion = usuarioKeyCloak.attributes["numeroIdentificacion"][0];
    } else {
      let partesUsr = usuarioKeyCloak.username.split(",");
      tipoIdentificacion = partesUsr[0];
      numeroIdentificacion = partesUsr[1];
    }

    return { tipoIdentificacion, numeroIdentificacion };
  }
}
